import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { apiClient } from '../api/client'
import { Card } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { PLAN_CONFIG, PG_PROVIDERS } from '../lib/constants'
import { CreditCard, Check, Coins, Clock } from 'lucide-react'
import { formatNumber, formatCurrency, formatDateTime } from '../lib/utils'
import type { PlanType, PGProvider, Payment } from '../types'

export function PaymentsPage() {
  const { t, i18n } = useTranslation()
  const isKorean = i18n.language === 'ko'

  const [selectedPlan, setSelectedPlan] = useState<PlanType | null>(null)
  const [selectedPG, setSelectedPG] = useState<PGProvider | null>(null)
  const [payments, setPayments] = useState<Payment[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingHistory, setIsLoadingHistory] = useState(true)

  useEffect(() => {
    fetchPaymentHistory()
  }, [])

  const fetchPaymentHistory = async () => {
    try {
      const data = await apiClient.getPaymentHistory(20, 0)
      setPayments(data.payments)
    } catch (error) {
      console.error('Failed to fetch payment history:', error)
    } finally {
      setIsLoadingHistory(false)
    }
  }

  const handleCharge = async () => {
    if (!selectedPlan || !selectedPG) return

    setIsLoading(true)
    try {
      const result = await apiClient.requestCharge({
        pg_provider: selectedPG,
        plan_type: selectedPlan,
      })

      // Redirect to payment approval URL
      window.location.href = result.approvalUrl
    } catch (error) {
      console.error('Failed to request charge:', error)
      alert(t('payments.failed_msg'))
    } finally {
      setIsLoading(false)
    }
  }

  const plans = Object.entries(PLAN_CONFIG) as [PlanType, typeof PLAN_CONFIG[PlanType]][]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">{t('payments.title')}</h1>
        <p className="text-light-text-muted dark:text-dark-text-muted mt-1">
          포인트를 충전하여 알림 서비스를 이용하세요
        </p>
      </div>

      {/* Plan Selection */}
      <Card title={t('payments.selectPlan')}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map(([key, plan]) => (
            <button
              key={key}
              onClick={() => setSelectedPlan(key)}
              className={`relative p-4 rounded-lg border-2 transition-all text-left ${
                selectedPlan === key
                  ? 'border-primary bg-primary/5'
                  : 'border-light-border dark:border-dark-border hover:border-primary/50'
              }`}
            >
              {selectedPlan === key && (
                <div className="absolute top-3 right-3 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}

              <p className="font-semibold text-lg">
                {isKorean ? plan.nameKo : plan.name}
              </p>

              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <CreditCard className="w-4 h-4 text-light-text-muted dark:text-dark-text-muted" />
                  <span>{formatCurrency(plan.amount)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Coins className="w-4 h-4 text-primary" />
                  <span className="font-medium text-primary">
                    {formatNumber(plan.points)}P
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-light-text-muted dark:text-dark-text-muted">
                  <Clock className="w-4 h-4" />
                  <span>{plan.duration_days}일 유효</span>
                </div>
              </div>

              {key === 'pro' && (
                <div className="mt-3">
                  <Badge variant="info">인기</Badge>
                </div>
              )}
            </button>
          ))}
        </div>
      </Card>

      {/* PG Selection */}
      <Card title={t('payments.selectPayment')}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(PG_PROVIDERS).map(([key, pg]) => (
            <button
              key={key}
              onClick={() => setSelectedPG(key as PGProvider)}
              className={`relative p-4 rounded-lg border-2 transition-all flex items-center gap-4 ${
                selectedPG === key
                  ? 'border-primary bg-primary/5'
                  : 'border-light-border dark:border-dark-border hover:border-primary/50'
              }`}
            >
              {selectedPG === key && (
                <div className="absolute top-3 right-3 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}

              <div className="text-3xl">{pg.icon}</div>
              <div>
                <p className="font-semibold">{isKorean ? pg.nameKo : pg.name}</p>
              </div>
            </button>
          ))}
        </div>
      </Card>

      {/* Charge Summary */}
      {selectedPlan && selectedPG && (
        <Card>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <p className="text-sm text-light-text-muted dark:text-dark-text-muted">
                {t('payments.amount')}
              </p>
              <p className="text-2xl font-bold">
                {formatCurrency(PLAN_CONFIG[selectedPlan].amount)}
              </p>
            </div>

            <div className="hidden md:block w-px h-12 bg-light-border dark:bg-dark-border" />

            <div className="text-center">
              <p className="text-sm text-light-text-muted dark:text-dark-text-muted">
                {t('payments.pointsToReceive')}
              </p>
              <p className="text-2xl font-bold text-primary">
                {formatNumber(PLAN_CONFIG[selectedPlan].points)}P
              </p>
            </div>

            <div className="hidden md:block w-px h-12 bg-light-border dark:bg-dark-border" />

            <div className="text-center md:text-right">
              <p className="text-sm text-light-text-muted dark:text-dark-text-muted">
                {t('payments.validity')}
              </p>
              <p className="text-lg font-medium">
                {PLAN_CONFIG[selectedPlan].duration_days}일
              </p>
            </div>

            <button
              onClick={handleCharge}
              disabled={isLoading}
              className="btn btn-primary px-8 py-3 text-lg flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t('payments.processing')}
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5" />
                  {t('payments.charge')}
                </>
              )}
            </button>
          </div>
        </Card>
      )}

      {/* Payment History */}
      <Card title={t('payments.history')}>
        {isLoadingHistory ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : payments.length === 0 ? (
          <div className="text-center py-8 text-light-text-muted dark:text-dark-text-muted">
            <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>결제 내역이 없습니다</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-light-border dark:border-dark-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-light-text-muted dark:text-dark-text-muted">
                    {t('payments.orderId')}
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-light-text-muted dark:text-dark-text-muted">
                    플랜
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-light-text-muted dark:text-dark-text-muted">
                    {t('payments.amount')}
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-light-text-muted dark:text-dark-text-muted">
                    {t('payments.status')}
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-light-text-muted dark:text-dark-text-muted">
                    날짜
                  </th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => (
                  <tr
                    key={payment.orderId}
                    className="border-b border-light-border dark:border-dark-border last:border-0"
                  >
                    <td className="py-3 px-4 text-sm font-mono">
                      {payment.orderId.slice(0, 16)}...
                    </td>
                    <td className="py-3 px-4 text-sm capitalize">
                      {payment.planType}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {formatCurrency(payment.amount)}
                    </td>
                    <td className="py-3 px-4">
                      <Badge
                        variant={
                          payment.status === 'completed'
                            ? 'success'
                            : payment.status === 'pending'
                            ? 'warning'
                            : 'danger'
                        }
                      >
                        {payment.status === 'completed'
                          ? t('payments.completed')
                          : payment.status === 'pending'
                          ? t('payments.pending')
                          : payment.status === 'cancelled'
                          ? t('payments.cancelled')
                          : t('payments.failed')}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {formatDateTime(payment.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
